import {Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges} from '@angular/core';
import {Subscription} from 'rxjs';
import {ObjectIdentifier} from '../../../../../core/shared/basic/object-identifier';
import {DmDocumentAssignmentService} from '../../../service/api/dm-document-assignment.service';
import {MvsCoreService} from '../../../../../core/shared/service/mvs-core.service';
import {DmDocumentDto} from '../../../model/dto/entity/dm-document.dto';
import {ToolingDocument} from '../../../interface/tooling-document.interface';
import {buildDmDocumentAssignmentRequest} from '../../../util/dm-document-assignment-request.util';

/**
 * DmDocumentToolingComponent
 *
 * Reusable component that displays documents assigned to any business object.
 * Supports configurable display modes (chips, dropdown, list) and an integrated
 * document preview via mvs-pdf-viewer. Optionally allows document upload.
 *
 * Data is loaded via DmDocumentAssignmentService using ObjectRequestList with
 * complex node joins to resolve document and document type information.
 */
@Component({
    selector: 'mvs-dm-document-tooling',
    templateUrl: './dm-document-tooling.component.html',
    styleUrls: ['./dm-document-tooling.component.scss'],
    standalone: false
})
export class DmDocumentToolingComponent implements OnInit, OnChanges, OnDestroy {

    // -- Inputs --
    @Input() objectIdentifier: ObjectIdentifier;
    @Input() displayMode: 'chips' | 'dropdown' | 'list' = 'chips';
    @Input() showDisplayModeToggle: boolean = true;
    @Input() mainDocumentTypeId: number;
    @Input() uploadDocumentTypeIds: number[] = [];
    @Input() showUpload: boolean = false;
    @Input() height: string = '100%';
    @Input() documentTypeFilter: number[] = [];
    @Input() excludeDocumentIds: number[] = [];

    // -- Outputs --
    @Output() onDocumentSelect = new EventEmitter<ToolingDocument>();
    @Output() onDocumentUpload = new EventEmitter<DmDocumentDto>();

    /**
     * How many documents this component is CURRENTLY SHOWING — emitted after every load, including
     * the empty and the failed one.
     *
     * <h3>Why a host may not count these documents itself</h3>
     * A host that renders this component next to its own counter has two numbers for one list. It can
     * build the same request ({@link buildDmDocumentAssignmentRequest} exists so it must), but it
     * still cannot see the post-read filtering applied here ({@code documentTypeFilter},
     * {@code excludeDocumentIds}) nor a read that failed and left the list empty. Taking the number
     * from the component that renders the list makes the badge and the list one answer instead of
     * two — which is the whole reason this output exists.
     */
    @Output() onDocumentCount = new EventEmitter<number>();

    // -- Internal State --
    documents: ToolingDocument[] = [];
    selectedDocument: ToolingDocument | null = null;
    showUploadDialog: boolean = false;
    initialized: boolean = false;
    busy: boolean = false;

    // -- Display Mode Toggle Options --
    displayModeOptions = [
        {icon: 'fa-regular fa-microchip', value: 'chips'},
        {icon: 'fa-regular fa-caret-down', value: 'dropdown'},
        {icon: 'fa-regular fa-list', value: 'list'}
    ];

    private dmDocumentAssignmentService: DmDocumentAssignmentService;
    private uploadSubscription: Subscription;

    constructor(private coreService: MvsCoreService) {
    }

    ngOnInit(): void {
        this.dmDocumentAssignmentService =
            <DmDocumentAssignmentService>this.coreService.getCrudService('dm.DmDocumentAssignment');

        this.uploadSubscription = this.dmDocumentAssignmentService.subjectDocumentUpload.subscribe(() => {
            if (this.initialized) {
                this.loadDocuments();
            }
        });

        this.loadDocuments();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!this.initialized) {
            return;
        }

        if (changes['objectIdentifier'] && !changes['objectIdentifier'].firstChange) {
            this.loadDocuments();
        }
    }

    ngOnDestroy(): void {
        this.uploadSubscription?.unsubscribe();
    }

    /**
     * Load document assignments for the given objectIdentifier.
     *
     * <p>The request comes from {@link buildDmDocumentAssignmentRequest} rather than being built here,
     * so a host that shows a counter for the same documents reads them through the SAME request. See
     * that function for what two hand-copied requests cost.</p>
     *
     * <p>Every terminal path reports the resulting size through {@link onDocumentCount} — the empty
     * one and the failed one included. A load state without a terminal branch is how a surface ends up
     * showing a stale number forever.</p>
     */
    loadDocuments(): void {
        if (!this.objectIdentifier) {
            this.documents = [];
            this.selectedDocument = null;
            this.initialized = true;
            this.onDocumentCount.emit(0);
            return;
        }

        this.busy = true;

        this.dmDocumentAssignmentService.list(buildDmDocumentAssignmentRequest(this.objectIdentifier)).subscribe({
            next: (result) => {
                this.documents = this.parseDocuments(result.entries || []);
                this.autoSelectDocument();
                this.busy = false;
                this.initialized = true;
                this.onDocumentCount.emit(this.documents.length);
            },
            error: () => {
                this.documents = [];
                this.selectedDocument = null;
                this.busy = false;
                this.initialized = true;
                this.onDocumentCount.emit(0);
            }
        });
    }

    /**
     * Parse raw DmDocumentAssignment entries into ToolingDocument array.
     * Applies documentTypeFilter and excludeDocumentIds filtering.
     *
     * <p>The resolved nodes are read with optional chaining ON PURPOSE. They used to be dereferenced
     * directly ({@code entry['documentDto']['dmDocumentTypeDto'].name}), so a single assignment whose
     * document or document type did not resolve threw INSIDE the {@code next} handler: the list was
     * never assigned, {@code busy} was never cleared, and the component span the spinner for good —
     * one unresolved row taking down the whole surface. Falling back per row keeps the other rows,
     * and the count reported to the host stays the count of what is on screen.</p>
     */
    private parseDocuments(entries: any[]): ToolingDocument[] {
        let parsed: ToolingDocument[] = entries.map(entry => {
            const documentType = entry?.['documentDto']?.['dmDocumentTypeDto'];
            return {
                assignmentId: entry.id,
                documentId: entry.documentDtoId,
                // Prefer document type name for meaningful labels; fall back to document name
                documentName: documentType?.name || entry.documentDtoName || 'Dokument',
                documentTypeName: documentType?.name || entry.documentDtoName || 'Dokument',
                documentTypeId: documentType?.id || entry.documentDtoId,
                mimeType: entry?.['documentDto']?.mimeType || '',
                lastModified: entry.lastModifiedDate || entry.createdDate || ''
            };
        });

        // Apply document type filter
        if (this.documentTypeFilter?.length) {
            parsed = parsed.filter(doc => this.documentTypeFilter.includes(doc.documentTypeId));
        }

        // Apply exclude filter
        if (this.excludeDocumentIds?.length) {
            parsed = parsed.filter(doc => !this.excludeDocumentIds.includes(doc.documentId));
        }

        return parsed;
    }

    /**
     * Auto-select a document after loading:
     * 1. If mainDocumentTypeId is set, select first matching document.
     * 2. Otherwise, select the first document.
     * 3. Emit onDocumentSelect.
     */
    private autoSelectDocument(): void {
        if (!this.documents.length) {
            this.selectedDocument = null;
            return;
        }

        let target: ToolingDocument | null = null;

        if (this.mainDocumentTypeId) {
            target = this.documents.find(doc => doc.documentTypeId === this.mainDocumentTypeId);
        }

        if (!target) {
            target = this.documents[0];
        }

        this.selectedDocument = target;
        this.onDocumentSelect.emit(this.selectedDocument);
    }

    /**
     * Handle document selection change from any display mode.
     */
    onDocumentChanged(document: ToolingDocument): void {
        if (!document) {
            return;
        }
        this.selectedDocument = document;
        this.onDocumentSelect.emit(this.selectedDocument);
    }

    /**
     * Handle document upload completion from the upload component.
     */
    handleDocumentUploaded(event: DmDocumentDto): void {
        this.showUploadDialog = false;
        this.onDocumentUpload.emit(event);
        this.loadDocuments();
    }
}
